import React, { useState, useEffect } from 'react';
import { IDENTITY } from '../data';
import { getGreeting } from '../hooks/useWeather';

const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$%&';

const PATH_CONTENT = {
  work: {
    roles: ['Data Systems Architect', 'Analytics Engineer', 'Builder', 'Systems Thinker', 'Senior Analyst'],
    tagline: 'I build systems that turn data into decisions — and startups that prove it.',
    cta1: { label: 'View My Experience', href: '#experience' },
    cta2: { label: 'See My Projects', href: '#projects' },
    sub: '6+ years · Fintech · AI · Data Engineering',
  },
  vision: {
    roles: ['AI Founder', 'Co-Founder @ Finide', 'Builder', 'Product Thinker', 'Startup Operator'],
    tagline: 'From physics to fintech — building intelligence into everything I touch.',
    cta1: { label: "See What I'm Building", href: '#projects' },
    cta2: { label: 'Read My Thinking', href: '#writing' },
    sub: 'Finide · Memories · CareHelp · SMART · 6 products shipped',
  },
};

function useDecodeText(target, delay = 0) {
  const [display, setDisplay] = useState(() =>
    target.split('').map(c => c === ' ' ? ' ' : CHARS[Math.floor(Math.random() * CHARS.length)]).join('')
  );
  useEffect(() => {
    const timeout = setTimeout(() => {
      let iteration = 0;
      const interval = setInterval(() => {
        setDisplay(target.split('').map((char, i) => {
          if (char === ' ') return ' ';
          if (i < iteration) return char;
          return CHARS[Math.floor(Math.random() * CHARS.length)];
        }).join(''));
        iteration += 0.5;
        if (iteration >= target.length) { clearInterval(interval); setDisplay(target); }
      }, 40);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timeout);
  }, [target, delay]);
  return display;
}

export default function Hero({ path }) {
  const content = PATH_CONTENT[path] || PATH_CONTENT.work;
  const decodedName = useDecodeText(IDENTITY.shortName, 200);
  const decodedTitle = useDecodeText('Data Systems Architect · AI Founder · Builder', 800);
  const [roleIndex, setRoleIndex] = useState(0);
  const [roleVisible, setRoleVisible] = useState(true);

  useEffect(() => { setRoleIndex(0); }, [path]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRoleVisible(false);
      setTimeout(() => { setRoleIndex(i => (i + 1) % content.roles.length); setRoleVisible(true); }, 400);
    }, 2800);
    return () => clearInterval(interval);
  }, [content.roles.length]);

  const now = new Date();
  const bostonHour = parseInt(new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }));
  const { greeting: greetText, note } = getGreeting(bostonHour, now.getDay());

  return (
    <section id="hero" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '80px 12% 0', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, opacity: 0.035, pointerEvents: 'none', backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")", backgroundSize: '256px' }} />

      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '40px', animation: 'fadeUp 0.6s ease both' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.25em', color: 'var(--ink-light)', textTransform: 'uppercase', border: '1px solid rgba(26,26,26,0.12)', padding: '5px 14px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: path === 'vision' ? 'var(--accent)' : 'var(--ink)', display: 'inline-block' }} />
          {path === 'work' ? 'Explore My Work — Recruiter View' : 'Explore My Vision — Founder View'}
        </div>
      </div>

      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.22em', color: 'var(--ink-light)', textTransform: 'uppercase', marginBottom: '36px', animation: 'fadeUp 0.8s ease 0.1s both' }}>
        {greetText} — <span style={{ opacity: 0.55 }}>{note}</span>
      </div>

      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(64px, 9vw, 140px)', fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 0.88, color: 'var(--ink)', marginBottom: '16px' }}>
        {decodedName}
      </h1>

      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'clamp(10px, 1.1vw, 13px)', letterSpacing: '0.08em', color: 'var(--ink-mid)', marginBottom: '28px', minHeight: '20px' }}>
        {decodedTitle}
      </div>

      <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(18px, 2.2vw, 28px)', fontWeight: 400, fontStyle: 'italic', color: 'var(--ink)', maxWidth: '580px', lineHeight: 1.35, marginBottom: '40px', borderLeft: '3px solid var(--accent)', paddingLeft: '20px', animation: 'fadeUp 0.8s ease 0.3s both' }}>
        "{content.tagline}"
      </div>

      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.2em', color: 'var(--ink-light)', textTransform: 'uppercase', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ width: '20px', height: '1px', background: 'var(--accent)', display: 'inline-block' }} />
        <span style={{ transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: roleVisible ? 1 : 0, transform: roleVisible ? 'translateY(0)' : 'translateY(6px)', display: 'inline-block', minWidth: '220px' }}>{content.roles[roleIndex]}</span>
      </div>

      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.18em', color: 'var(--ink-light)', textTransform: 'uppercase', marginBottom: '44px', opacity: 0.6, animation: 'fadeUp 0.8s ease 0.5s both' }}>
        {content.sub}
      </div>

      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', animation: 'fadeUp 0.8s ease 0.6s both' }}>
        <a href={content.cta1.href} style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.2em', color: 'var(--bg)', background: 'var(--ink)', padding: '14px 28px', textDecoration: 'none', textTransform: 'uppercase', transition: 'opacity 0.2s' }} onMouseEnter={e => e.target.style.opacity = '0.8'} onMouseLeave={e => e.target.style.opacity = '1'}>{content.cta1.label}</a>
        <a href={content.cta2.href} style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.2em', color: 'var(--ink)', border: '1px solid rgba(26,26,26,0.2)', padding: '14px 28px', textDecoration: 'none', textTransform: 'uppercase', transition: 'border-color 0.2s' }} onMouseEnter={e => e.target.style.borderColor = 'var(--ink)'} onMouseLeave={e => e.target.style.borderColor = 'rgba(26,26,26,0.2)'}>{content.cta2.label}</a>
        <a href="/" onClick={e => { e.preventDefault(); window.location.reload(); }} style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', letterSpacing: '0.15em', color: 'var(--ink-light)', textDecoration: 'none', textTransform: 'uppercase', padding: '14px 0', display: 'flex', alignItems: 'center', gap: '6px', transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--ink)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-light)'}>↩ Switch view</a>
      </div>

      <div style={{ position: 'absolute', bottom: '40px', left: '12%', fontFamily: 'var(--font-mono)', fontSize: '9px', letterSpacing: '0.3em', color: 'var(--ink-light)', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '12px', animation: 'fadeUp 1s ease 1.2s both' }}>
        <span>Scroll</span><div style={{ width: '40px', height: '1px', background: 'var(--ink-light)' }} />
      </div>

      <style>{`@keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </section>
  );
}
